import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import Button from '../components/ui/Button';
import Icons from '../components/Icons';
import SearchableSelect from '../components/ui/SearchableSelect';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import ExternalMessageService from '../services/ExternalMessageService';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';

const buildDoc = () => ({
  id: `doc-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  file: null,
  expiryDate: '',
  fileName: '',
  linkUrl: '',
  archived: false
});

const buildTemplate = () => ({
  id: `tpl-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  duration: '',
  focus: ''
});

const buildLessonPlan = () => ({
  id: `plan-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  date: '',
  file: null,
  notes: '',
  fileName: '',
  linkUrl: '',
  archived: false
});

export default function ProgrammesEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const programmeService = new ProgrammeService();
  const { students } = useStudentStore();
  const { user } = useUser();
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );
  const [programmes, setProgrammes] = useState(() => programmeService.list());
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    name: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    days: [],
    time: '',
    capacity: '',
    location: '',
    staff: '',
    staffAvailability: '',
    courseFee: '',
    eventFee: '',
    otherFee: ''
  });
  const [templates, setTemplates] = useState([buildTemplate()]);
  const [documents, setDocuments] = useState([buildDoc()]);
  const [lessonPlans, setLessonPlans] = useState([buildLessonPlan()]);
  const [linkStatus, setLinkStatus] = useState('');

  const dayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const parseProgrammeDays = (dayString) => {
    if (!dayString) return [];
    return dayString
      .replace(/&/g, '/')
      .replace(/,/g, '/')
      .replace(/\s+/g, '')
      .split('/')
      .map((part) => part.slice(0, 3))
      .filter(Boolean)
      .map((day) => day.charAt(0).toUpperCase() + day.slice(1).toLowerCase());
  };

  const programme = programmes.find((item) => item.id === id);
  const activeDocuments = documents.filter((doc) => !doc.archived);
  const archivedDocuments = documents.filter((doc) => doc.archived);
  const activeLessonPlans = lessonPlans.filter((plan) => !plan.archived);
  const archivedLessonPlans = lessonPlans.filter((plan) => plan.archived);

  useEffect(() => {
    if (!programme) return;
    setForm({
      name: programme.name || '',
      startDate: programme.startDate || new Date().toISOString().slice(0, 10),
      endDate: programme.endDate || '',
      days: programme.days?.length ? programme.days : parseProgrammeDays(programme.day),
      time: programme.time || '',
      capacity: programme.capacity ? String(programme.capacity) : '',
      location: programme.location || '',
      staff: programme.staff || '',
      staffAvailability: programme.staffAvailability || '',
      courseFee: programme.fees?.courseFee ? String(programme.fees.courseFee) : '',
      eventFee: programme.fees?.eventFee ? String(programme.fees.eventFee) : '',
      otherFee: programme.fees?.otherFee ? String(programme.fees.otherFee) : ''
    });

    setTemplates(
      programme.templates?.length
        ? programme.templates.map((tpl) => ({
            id: tpl.id || `tpl-${Math.random().toString(36).slice(2, 8)}`,
            title: tpl.title || '',
            duration: tpl.duration || '',
            focus: tpl.focus || ''
          }))
        : [buildTemplate()]
    );

    setDocuments(
      programme.documents?.length
        ? programme.documents.map((doc) => ({
            id: doc.id || `doc-${Math.random().toString(36).slice(2, 8)}`,
            name: doc.name || '',
            file: null,
            expiryDate: doc.expiryDate || '',
            fileName: doc.fileName || '',
            linkUrl: doc.linkUrl || '',
            archived: Boolean(doc.archived)
          }))
        : [buildDoc()]
    );

    setLessonPlans(
      programme.lessonPlans?.length
        ? programme.lessonPlans.map((plan) => ({
            id: plan.id || `plan-${Math.random().toString(36).slice(2, 8)}`,
            title: plan.title || '',
            date: plan.date || '',
            file: null,
            notes: plan.notes || '',
            fileName: plan.fileName || '',
            linkUrl: plan.linkUrl || '',
            archived: Boolean(plan.archived)
          }))
        : [buildLessonPlan()]
    );
  }, [programme]);

  const toggleDay = (day) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day]
    }));
  };

  const addTemplate = () => setTemplates((prev) => [...prev, buildTemplate()]);
  const removeTemplate = (templateId) => setTemplates((prev) => prev.filter((item) => item.id !== templateId));
  const updateTemplate = (templateId, updates) =>
    setTemplates((prev) => prev.map((item) => (item.id === templateId ? { ...item, ...updates } : item)));

  const addDocument = () => setDocuments((prev) => [...prev, buildDoc()]);
  const removeDocument = (docId) => setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
  const updateDocument = (docId, updates) =>
    setDocuments((prev) => prev.map((doc) => (doc.id === docId ? { ...doc, ...updates } : doc)));

  const addLessonPlan = () => setLessonPlans((prev) => [...prev, buildLessonPlan()]);
  const removeLessonPlan = (planId) => setLessonPlans((prev) => prev.filter((plan) => plan.id !== planId));
  const updateLessonPlan = (planId, updates) =>
    setLessonPlans((prev) => prev.map((plan) => (plan.id === planId ? { ...plan, ...updates } : plan)));

  const handlePasteLink = async (applyLink) => {
    if (!navigator?.clipboard?.readText) {
      setLinkStatus('Clipboard access is not available in this browser.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        applyLink(text.trim());
        setLinkStatus('Link pasted from clipboard.');
      }
    } catch (error) {
      setLinkStatus('Unable to read clipboard. Please paste manually.');
    }
  };

  const shareLinkToStudents = ({ title, linkUrl, typeLabel }) => {
    if (!linkUrl) {
      setLinkStatus('Add a link before sharing with students.');
      return;
    }
    if (!user) {
      setLinkStatus('Sign in to share links.');
      return;
    }
    const programmeName = form.name.trim() || programme?.name || '';
    const recipients = students
      .filter((student) => (student.enrolments || []).some((enrol) => enrol.programme === programmeName))
      .map((student) => ({ id: student.id, name: student.name, type: 'student' }));
    if (recipients.length === 0) {
      setLinkStatus('No enrolled students to notify.');
      return;
    }
    messageService.sendFromStaff({
      subject: `${typeLabel} shared: ${title}`,
      body: `A new ${typeLabel.toLowerCase()} has been shared for ${programmeName}.\n\nAccess: ${linkUrl}`,
      fromUser: user,
      recipients,
      category: 'class-notes',
      references: [{ type: 'programme', id: programme?.id || id, label: programmeName }]
    });
    setLinkStatus('Link shared with enrolled students.');
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!programme) return;
    if (!form.name.trim()) {
      setFormError('Programme name is required.');
      return;
    }
    if (!form.capacity) {
      setFormError('Capacity is required.');
      return;
    }
    if (form.days.length === 0) {
      setFormError('Select at least one day.');
      return;
    }

    const validTemplates = templates.filter((tpl) => tpl.title.trim());
    const validDocuments = documents
      .filter((doc) => doc.name.trim() || doc.file || doc.linkUrl)
      .map((doc) => ({
        id: doc.id,
        name: doc.name.trim() || (doc.file ? doc.file.name : 'Document'),
        expiryDate: doc.expiryDate || null,
        fileName: doc.file ? doc.file.name : doc.fileName || null,
        linkUrl: doc.linkUrl || '',
        archived: Boolean(doc.archived),
        uploadedAt: new Date().toISOString()
      }));
    const validLessonPlans = lessonPlans
      .filter((plan) => plan.title.trim() || plan.file || plan.linkUrl)
      .map((plan) => ({
        id: plan.id,
        title: plan.title.trim() || (plan.file ? plan.file.name : 'Lesson plan'),
        date: plan.date || null,
        fileName: plan.file ? plan.file.name : plan.fileName || null,
        notes: plan.notes || '',
        linkUrl: plan.linkUrl || '',
        archived: Boolean(plan.archived),
        uploadedAt: new Date().toISOString()
      }));

    const updatedProgramme = {
      ...programme,
      name: form.name.trim(),
      day: form.days.join('/'),
      time: form.time || 'TBD',
      startDate: form.startDate,
      endDate: form.endDate || null,
      capacity: Number(form.capacity),
      staff: form.staff || 'Unassigned',
      location: form.location || 'TBD',
      days: form.days,
      staffAvailability: form.staffAvailability || '',
      fees: {
        courseFee: Number(form.courseFee || 0),
        eventFee: Number(form.eventFee || 0),
        otherFee: Number(form.otherFee || 0)
      },
      templates: validTemplates.map((tpl) => ({
        id: tpl.id,
        title: tpl.title.trim(),
        duration: tpl.duration.trim(),
        focus: tpl.focus.trim()
      })),
      documents: validDocuments,
      lessonPlans: validLessonPlans
    };

    const next = programmeService.update(updatedProgramme);
    setProgrammes(next);
    navigate('/programmes/overview');
  };

  if (!programme) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Programme not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/programmes/overview')}>
          Back to overview
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Edit Programme</h2>
          <p className="sp-muted">Update schedule, capacity, fees, and documentation.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/programmes/overview')}>
            Back to overview
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSave} className="space-y-4">
          {formError && <div className="sp-panel sp-panel--muted p-3 text-sm">{formError}</div>}

          <div>
            <label className="sp-label">Programme name</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Start date</label>
              <DateSelect
                value={form.startDate}
                onChange={(value) => setForm({ ...form, startDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">End date</label>
              <DateSelect
                value={form.endDate}
                onChange={(value) => setForm({ ...form, endDate: value })}
                className="mt-2 w-full"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Days</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {dayOptions.map((day) => (
                  <label key={day} className="flex items-center gap-2 text-sm sp-muted">
                    <input
                      type="checkbox"
                      checked={form.days.includes(day)}
                      onChange={() => toggleDay(day)}
                    />
                    <span>{day}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="sp-label">Time</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.time}
                onChange={(event) => setForm({ ...form, time: event.target.value })}
              />
              <label className="sp-label mt-4 block">Capacity</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.capacity}
                onChange={(event) => setForm({ ...form, capacity: event.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Location</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Assign support worker</label>
              <SearchableSelect
                value={form.staff}
                options={staffOptions}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search support worker"
                className="mt-2"
                onChange={(value) => setForm({ ...form, staff: value })}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="sp-label">Course fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.courseFee}
                onChange={(event) => setForm({ ...form, courseFee: event.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Event fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.eventFee}
                onChange={(event) => setForm({ ...form, eventFee: event.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Other fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.otherFee}
                onChange={(event) => setForm({ ...form, otherFee: event.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Staff availability</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.staffAvailability}
              onChange={(event) => setForm({ ...form, staffAvailability: event.target.value })}
            />
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Session Templates</h4>
                <p className="text-xs sp-muted">Define repeatable lesson templates for this programme.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addTemplate}>
                Add template
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {templates.map((template, index) => (
                <div key={template.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Title</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.title}
                      onChange={(event) => updateTemplate(template.id, { title: event.target.value })}
                      placeholder={`Session ${index + 1}`}
                    />
                  </div>
                  <div>
                    <label className="sp-label">Duration</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.duration}
                      onChange={(event) => updateTemplate(template.id, { duration: event.target.value })}
                      placeholder="e.g. 90 mins"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Focus area</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.focus}
                      onChange={(event) => updateTemplate(template.id, { focus: event.target.value })}
                      placeholder="e.g. Life skills"
                    />
                  </div>
                  {templates.length > 1 && (
                    <div className="md:col-span-3 flex justify-end">
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeTemplate(template.id)}>
                        Remove template
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Programme Documents</h4>
                <p className="text-xs sp-muted">Upload supporting documents with optional expiry dates.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addDocument}>
                Add document
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {activeDocuments.map((doc) => (
                <div key={doc.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Document name</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={doc.name}
                      onChange={(event) => updateDocument(doc.id, { name: event.target.value })}
                      placeholder="e.g. Risk assessment"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Upload</label>
                    <input
                      type="file"
                      className="sp-input mt-2 w-full"
                      onChange={(event) =>
                        updateDocument(doc.id, { file: event.target.files?.[0] || null })
                      }
                    />
                    {(doc.file || doc.fileName) && (
                      <p className="text-xs sp-muted mt-1">{doc.file?.name || doc.fileName}</p>
                    )}
                  </div>
                  <div>
                    <label className="sp-label">Expiry date</label>
                    <DateSelect
                      value={doc.expiryDate}
                      onChange={(value) => updateDocument(doc.id, { expiryDate: value })}
                      className="mt-2 w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sp-label">Share link</label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        className="sp-input w-full"
                        value={doc.linkUrl || ''}
                        onChange={(event) => updateDocument(doc.id, { linkUrl: event.target.value })}
                        placeholder="https://drive.google.com/..."
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Paste link from clipboard"
                        onClick={() => handlePasteLink((text) => updateDocument(doc.id, { linkUrl: text }))}
                      >
                        <Icons.Clipboard />
                      </Button>
                    </div>
                    <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="sp-label">Archive</label>
                    <input
                      type="checkbox"
                      checked={Boolean(doc.archived)}
                      onChange={(event) => updateDocument(doc.id, { archived: event.target.checked })}
                      aria-label="Archive document"
                    />
                  </div>
                  <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        shareLinkToStudents({
                          title: doc.name || doc.fileName || 'Document',
                          linkUrl: doc.linkUrl,
                          typeLabel: 'Document'
                        })
                      }
                    >
                      Share link to students
                    </Button>
                    {documents.length > 1 && (
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeDocument(doc.id)}>
                        Remove document
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {archivedDocuments.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-sm">
                  <div className="sp-value" style={{ fontWeight: 600 }}>Archived documents</div>
                  <div className="mt-2 space-y-2">
                    {archivedDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between gap-2">
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{doc.name || doc.fileName || 'Document'}</div>
                          <div className="text-xs sp-muted">{doc.linkUrl || 'No link added'}</div>
                        </div>
                        <Button size="sm" variant="secondary" type="button" onClick={() => updateDocument(doc.id, { archived: false })}>
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Lesson Plans</h4>
                <p className="text-xs sp-muted">Upload lesson plans and resources for this programme.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addLessonPlan}>
                Add lesson plan
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {activeLessonPlans.map((plan) => (
                <div key={plan.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Title</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={plan.title}
                      onChange={(event) => updateLessonPlan(plan.id, { title: event.target.value })}
                      placeholder="e.g. Week 1 plan"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Session date</label>
                    <DateSelect
                      value={plan.date}
                      onChange={(value) => updateLessonPlan(plan.id, { date: value })}
                      className="mt-2 w-full"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Upload</label>
                    <input
                      type="file"
                      className="sp-input mt-2 w-full"
                      onChange={(event) =>
                        updateLessonPlan(plan.id, { file: event.target.files?.[0] || null })
                      }
                    />
                    {(plan.file || plan.fileName) && <p className="text-xs sp-muted mt-1">{plan.file?.name || plan.fileName}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label className="sp-label">Share link</label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        className="sp-input w-full"
                        value={plan.linkUrl || ''}
                        onChange={(event) => updateLessonPlan(plan.id, { linkUrl: event.target.value })}
                        placeholder="https://drive.google.com/..."
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Paste link from clipboard"
                        onClick={() => handlePasteLink((text) => updateLessonPlan(plan.id, { linkUrl: text }))}
                      >
                        <Icons.Clipboard />
                      </Button>
                    </div>
                    <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="sp-label">Archive</label>
                    <input
                      type="checkbox"
                      checked={Boolean(plan.archived)}
                      onChange={(event) => updateLessonPlan(plan.id, { archived: event.target.checked })}
                      aria-label="Archive lesson plan"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="sp-label">Notes</label>
                    <textarea
                      className="sp-textarea mt-2 w-full"
                      rows={2}
                      value={plan.notes}
                      onChange={(event) => updateLessonPlan(plan.id, { notes: event.target.value })}
                      placeholder="Key outcomes, resources, or homework."
                    />
                  </div>
                  <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        shareLinkToStudents({
                          title: plan.title || plan.fileName || 'Lesson plan',
                          linkUrl: plan.linkUrl,
                          typeLabel: 'Lesson plan'
                        })
                      }
                    >
                      Share link to students
                    </Button>
                    {lessonPlans.length > 1 && (
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeLessonPlan(plan.id)}>
                        Remove lesson plan
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {archivedLessonPlans.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-sm">
                  <div className="sp-value" style={{ fontWeight: 600 }}>Archived lesson plans</div>
                  <div className="mt-2 space-y-2">
                    {archivedLessonPlans.map((plan) => (
                      <div key={plan.id} className="flex items-center justify-between gap-2">
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{plan.title || plan.fileName || 'Lesson plan'}</div>
                          <div className="text-xs sp-muted">{plan.linkUrl || 'No link added'}</div>
                        </div>
                        <Button size="sm" variant="secondary" type="button" onClick={() => updateLessonPlan(plan.id, { archived: false })}>
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {linkStatus && <div className="text-xs sp-muted mt-2">{linkStatus}</div>}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => navigate('/programmes/overview')}>
              Cancel
            </Button>
            <Button type="submit">Save changes</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
